import { observer } from 'mobx-react-lite'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import {
  FormFieldGroup,
  FormFieldSet,
  FormFieldSetLegend,
} from '@/components/Form'
import { COUNTRIES, COUNTRY_US } from '@/assets/data/countries'
import { US_STATES_AND_TERRITORIES } from '@/assets/data/us-states-territories'
import type { AddressFieldSetViewModel } from './AddressFieldSetViewModel'
import { FormFieldRequiredLabel } from '../FormFieldRequiredLabel'

export { AddressFieldSetViewModel } from './AddressFieldSetViewModel'
export { type ContactAddressResource } from '@taxfyle/txf-api-client'

interface AddressFieldSetProps {
  model: AddressFieldSetViewModel
  legend?: string
  testId?: string
}

export const AddressFieldSet = observer(function AddressFieldSet({
  model,
  legend = 'Address',
  testId,
}: AddressFieldSetProps) {
  return (
    <FormFieldSet>
      <FormFieldSetLegend>{legend}</FormFieldSetLegend>
      <FormFieldGroup stackWhenCompact>
        <TextFieldContainer
          autoComplete="address-line1"
          model={model}
          field="streetLine"
          label={
            <FormFieldRequiredLabel
              label="Street address"
              required={model.required}
            />
          }
          placeholder="e.g. 123 Main St"
          testId={testId ? `${testId}-street-input` : undefined}
        />
        <TextFieldContainer
          autoComplete="address-level2"
          model={model}
          field="city"
          label={
            <FormFieldRequiredLabel label="City" required={model.required} />
          }
          placeholder="e.g. New York"
          testId={testId ? `${testId}-city-input` : undefined}
        />
        <TextFieldContainer
          autoComplete="postal-code"
          model={model}
          field="postalCode"
          label={
            <FormFieldRequiredLabel
              label="Postal code"
              required={model.required}
            />
          }
          placeholder="e.g. 10001"
          testId={testId ? `${testId}-postal-code-input` : undefined}
        />
      </FormFieldGroup>
      <FormFieldGroup stackWhenCompact>
        <SelectContainer
          autoComplete="country"
          model={model}
          field="country"
          label={
            <FormFieldRequiredLabel label="Country" required={model.required} />
          }
          items={COUNTRIES}
          getKey={([code]) => code}
          getLabel={([, label]) => label}
          testId={testId ? `${testId}-country-select` : undefined}
        />
        {model.country === COUNTRY_US[0] ? (
          <SelectContainer
            autoComplete="address-level1"
            model={model}
            field="region"
            label={
              <FormFieldRequiredLabel label="State" required={model.required} />
            }
            items={US_STATES_AND_TERRITORIES}
            getKey={([code]) => code}
            getLabel={([, label]) => label}
            testId={testId ? `${testId}-state-select` : undefined}
          />
        ) : (
          <TextFieldContainer
            autoComplete="address-level1"
            model={model}
            field="region"
            label={
              <FormFieldRequiredLabel
                label="Region"
                required={model.required}
              />
            }
            testId={testId ? `${testId}-region-input` : undefined}
          />
        )}
      </FormFieldGroup>
    </FormFieldSet>
  )
})
